import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * RouteProgress — 2px bronze progress bar (NProgress-style) that appears
 * ONLY when a route transition takes longer than 300ms. Silent for fast
 * transitions (the common case).
 *
 * Why we start the timer on CLICK, not on pathname change:
 *   A useEffect keyed on useLocation() only fires AFTER React has committed
 *   the new route — i.e. after all the slow work is already done. To
 *   detect a genuinely slow transition we must start the 300ms timer at
 *   the moment the user expresses navigation intent (link click, popstate)
 *   and complete it once the new pathname has painted.
 *
 * Behaviour:
 *   1. Global capture-phase click listener → detect same-origin link clicks
 *      and start a 300ms setTimeout.
 *   2. If pathname changes before 300ms → cancel the timer silently.
 *   3. If 300ms elapses first → fade the bar in and trickle 15% → 80%.
 *   4. When pathname finally changes (post-paint) → snap 100%, fade out.
 *
 * Accessibility:
 *   - role="progressbar" + aria-valuenow so screen readers can announce.
 *   - aria-hidden while idle to avoid noise.
 *   - Respects prefers-reduced-motion (transitions disabled).
 */
const THRESHOLD_MS = 300;

const RouteProgress = () => {
  const { pathname } = useLocation();
  const [state, setState] = useState({ visible: false, progress: 0 });
  const showTimerRef = useRef(null);
  const trickleRef = useRef(null);
  const completeTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const firstRenderRef = useRef(true);
  const visibleRef = useRef(false); // mirror to read from timer callbacks

  const clearAll = useCallback(() => {
    if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null; }
    if (trickleRef.current) { clearInterval(trickleRef.current); trickleRef.current = null; }
    if (completeTimerRef.current) { clearTimeout(completeTimerRef.current); completeTimerRef.current = null; }
    if (fadeTimerRef.current) { clearTimeout(fadeTimerRef.current); fadeTimerRef.current = null; }
  }, []);

  const startShowTimer = useCallback(() => {
    clearAll();
    showTimerRef.current = setTimeout(() => {
      visibleRef.current = true;
      setState({ visible: true, progress: 15 });
      // Trickle asymptotically toward ~80% while we wait for the new route
      trickleRef.current = setInterval(() => {
        setState((s) => {
          if (s.progress >= 80) return s;
          const next = s.progress + Math.max(1, (80 - s.progress) * 0.15);
          return { ...s, progress: next };
        });
      }, 200);
    }, THRESHOLD_MS);
  }, [clearAll]);

  const completeIfVisible = useCallback(() => {
    clearAll();
    if (!visibleRef.current) {
      // Fast transition — bar never showed; reset silently
      setState({ visible: false, progress: 0 });
      return;
    }
    // Snap to 100%, then fade out and reset
    setState({ visible: true, progress: 100 });
    completeTimerRef.current = setTimeout(() => {
      visibleRef.current = false;
      setState((s) => ({ ...s, visible: false }));
      fadeTimerRef.current = setTimeout(
        () => setState({ visible: false, progress: 0 }),
        250
      );
    }, 200);
  }, [clearAll]);

  // Global click + popstate listeners — start the timer BEFORE the URL changes.
  useEffect(() => {
    const onClick = (e) => {
      // Only untargeted left-clicks trigger navigation
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target.closest && e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href) return;
      // Skip absolute external, non-navigational schemes, downloads, blank targets
      if (/^https?:\/\//.test(href) && !href.startsWith(window.location.origin)) return;
      if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      // Same-page anchor navigation — skip
      try {
        const url = new URL(href, window.location.origin);
        if (url.pathname === window.location.pathname && url.hash) return;
      } catch (_) { /* invalid href — proceed */ }
      startShowTimer();
    };
    const onPopState = () => startShowTimer();
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPopState);
      clearAll();
    };
  }, [startShowTimer, clearAll]);

  // Pathname change → new route has been committed. Wait one paint, then complete.
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    const raf1 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(completeIfVisible);
    });
    return () => window.cancelAnimationFrame(raf1);
  }, [pathname, completeIfVisible]);

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      data-testid="route-progress-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={state.visible ? Math.round(state.progress) : 0}
      aria-label="טוען עמוד"
      aria-hidden={!state.visible}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        zIndex: 9999,
        pointerEvents: 'none',
        opacity: state.visible ? 1 : 0,
        transition: reducedMotion ? 'none' : 'opacity 220ms ease',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${state.progress}%`,
          background: 'linear-gradient(90deg, #B08D57 0%, #D4B37A 50%, #B08D57 100%)',
          boxShadow: '0 0 8px rgba(176, 141, 87, 0.6), 0 0 2px rgba(176, 141, 87, 0.8)',
          transition: reducedMotion ? 'none' : 'width 220ms ease-out',
        }}
      />
    </div>
  );
};

export default RouteProgress;
